import { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
// import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { Html5QrcodeScanner } from "html5-qrcode";
import useOdooApi from "../../../hooks/useOdooApi";
import type { PatientInfo, CheckInBarcodeResponse, BarcodeScannerProps } from "../../../types";

export default function BarcodeScanner({ onScanSuccess }: BarcodeScannerProps) {
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const { t } = useTranslation();
  const odooApi = useOdooApi();

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);

  // useEffect(() => {
  //   startScanning();
  // }, []);

  const startScanning = async () => {
    setScanning(true);
    setError(null);

    try {
      // Check camera permission first
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        videoConstraints: {
          facingMode: "environment", // Use back camera if available
        },
      };

      const scanner = new Html5QrcodeScanner("qr-reader", config, false);
      scannerRef.current = scanner;

      scanner.render(
        async (decodedText: string) => {
          console.log("QR Code scanned:", decodedText);
          scanner.clear();
          setScanning(false);
          setLoading(true);

          try {
            // Call checkInBarcode API with correct parameter name
            // @ts-ignore
            const response = await odooApi.checkInBarcode({ booking_number: decodedText }) as CheckInBarcodeResponse;

            if (response.code === "success") {
              // Transform API response to PatientInfo format
              const patientInfo: PatientInfo = {
                booking_id: response.booking_id,
                name: response.full_name || "Unknown",
                phone: response.phone || "Unknown",
                email: response.email || "Unknown",
                appointmentDate: response.register_date || "Unknown",
                location: response.event_name || "Unknown",
                id: response.pid || "Unknown",
                age: response.age || 0,
                imageBarcode: response.image_barcode ? `data:image/jpg;base64,${response.image_barcode}` : undefined,
                is_question: response.is_question || false,
                is_pdpa_sign: response.is_pdpa_sign || false,
                patient_id: response.patient_id,
              };

              setLoading(false);
              onScanSuccess(decodedText, patientInfo);
            } else {
              throw new Error(response.message || "Unknown error occurred");
            }
          } catch (error: unknown) {
            console.error("Check-in API error:", error);
            setLoading(false);
            const errorMessage = error instanceof Error ? error.message : "Failed to verify barcode. Please try again.";
            setError(errorMessage);
          }
        },
        (error: string) => {
          console.warn("QR Code scan error:", error);
          // Don't show all scan errors to user, they're too frequent
        }
      );
    } catch (error) {
      console.error("Camera permission error:", error);
      setError(t("admin.checkin.cameraError"));
      setScanning(false);
    }
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.clear();
      scannerRef.current = null;
    }
    setScanning(false);
  };

  // const useMockData = async () => {
  //   setLoading(true);

  //   try {
  //     // Use a mock booking number for testing
  //     const mockBookingNumber = "B25080553";
  //     const response = await odooApi.checkInBarcode({ booking_number: mockBookingNumber }) as CheckInBarcodeResponse;

  //     if (response.code === "success") {
  //       const patientInfo: PatientInfo = {
  //         name: response.full_name || "Mrs Suda Lapklang",
  //         phone: response.phone || "084713819",
  //         email: response.email || "suda.lup@gmail.com",
  //         appointmentDate: response.register_date || "08/08/2025 16:29",
  //         location: response.event_name || "สพษ.มะค่าระบง",
  //         id: response.pid || "3101500599658",
  //       };

  //       setLoading(false);
  //       onScanSuccess(mockBookingNumber, patientInfo);
  //     } else {
  //       throw new Error(response.message || "Mock data failed");
  //     }
  //   } catch (error: unknown) {
  //     console.error("Mock data error:", error);
  //     setLoading(false);

  //     // Fallback to original mock data if API fails
  //     const mockPatientInfo = {
  //       name: "Mrs Suda Lapklang",
  //       phone: "084713819",
  //       email: "suda.lup@gmail.com",
  //       appointmentDate: "08/08/2025 16:29",
  //       location: "สพษ.มะค่าระบง",
  //       id: "3101500599658",
  //     };

  //     onScanSuccess("B25080553", mockPatientInfo);
  //   }
  // };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ minHeight: "60vh", p: 3 }}
      >
        <Typography
          variant="h4"
          sx={{ color: "#7479AE", fontWeight: 700, mb: 4, textAlign: "center" }}
        >
          {t("admin.checkin.title")}
        </Typography>

        {!scanning && !loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Button
              variant="contained"
              size="large"
              onClick={startScanning}
              sx={{
                bgcolor: "#7479AE",
                color: "white",
                borderRadius: "50px !important",
                mt: 5,
                minHeight: "48px",
                width: "100%",
                maxWidth: "300px",
                fontSize: 24,

              }}
              // startIcon={<QrCodeScannerIcon />}
            >
              {t("admin.checkin.scanBarcode")}
            </Button>

            {/* <Typography
              variant="body2"
              sx={{ color: "#666", textAlign: "center" }}
            >
              {t("admin.checkin.or")}
            </Typography>

            <Button
              variant="outlined"
              onClick={useMockData}
              sx={{
                color: "#7479AE",
                borderColor: "#7479AE",
                borderRadius: 3,
                py: 1.5,
                px: 3,
                "&:hover": {
                  bgcolor: "#f5f5f5",
                  borderColor: "#5a5f8a",
                },
              }}
            >
              {t("admin.checkin.useMockData")}
            </Button> */}
          </Box>
        ) : loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <CircularProgress
              size={60}
              sx={{ color: "#7479AE" }}
            />
            <Typography
              variant="body1"
              sx={{ color: "#7479AE", textAlign: "center" }}
            >
              {t("admin.checkin.verifying")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <div id="qr-reader" style={{ width: "100%", borderRadius: "16px", overflow: "hidden" }}></div>
            <Button
              variant="outlined"
              onClick={stopScanning}
              sx={{ mt: 2, color: "#7479AE", borderColor: "#7479AE", display: "none" }}
              fullWidth
            >
              {t("admin.checkin.stopScanning")}
            </Button>
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2, width: "100%", maxWidth: 400 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                {t("admin.checkin.close")}
              </Button>
            }
          >
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                {t("admin.checkin.troubleshootTip")}
              </Typography>
            </Box>
          </Alert>
        )}
      </Box>
    </motion.div>
  );
}
